import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Sparkles,
  FileText,
  CheckCircle2,
  Users,
  CalendarDays,
  AlertTriangle,
  ListChecks,
  RefreshCw,
  Copy,
  Check,
  Loader2,
} from "lucide-react";
import { documentsApi } from "../../services/api";

interface DocumentSummaryData {
  summary: string;
  keyPoints: string[];
  documentType: string;
  parties: string[];
  dates: string[];
  risks: string[];
  actionItems: string[];
  generatedAt?: string;
  modelUsed?: string;
}

interface Props {
  documentId: string;
  documentTitle?: string;
  onClose?: () => void;
}

const typeColors: Record<string, string> = {
  contract: "bg-blue-100 text-blue-800",
  agreement: "bg-blue-100 text-blue-800",
  motion: "bg-purple-100 text-purple-800",
  letter: "bg-slate-100 text-slate-700",
  deed: "bg-amber-100 text-amber-800",
  will: "bg-indigo-100 text-indigo-800",
  affidavit: "bg-teal-100 text-teal-800",
  lease: "bg-cyan-100 text-cyan-800",
  pleading: "bg-rose-100 text-rose-800",
  memorandum: "bg-violet-100 text-violet-800",
};

function getTypeColor(docType: string): string {
  const key = docType.toLowerCase();
  return typeColors[key] || "bg-emerald-100 text-emerald-800";
}

function formatTimestamp(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const AiDocumentSummary: React.FC<Props> = ({ documentId, documentTitle, onClose }) => {
  const [summary, setSummary] = useState<DocumentSummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const fetchCachedSummary = useCallback(async () => {
    try {
      const res = await documentsApi.aiGetSummary(documentId);
      setSummary(res.data.data);
      setError(null);
      return true;
    } catch {
      return false;
    }
  }, [documentId]);

  const generateSummary = useCallback(async () => {
    setGenerating(true);
    setError(null);
    try {
      const res = await documentsApi.aiSummarize(documentId);
      setSummary(res.data.data);
    } catch (err: any) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "Failed to generate summary. Please try again.";
      setError(msg);
    } finally {
      setGenerating(false);
    }
  }, [documentId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const hasCached = await fetchCachedSummary();
      if (!cancelled && !hasCached) {
        await generateSummary();
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchCachedSummary, generateSummary]);

  const handleRegenerate = async () => {
    setLoading(false);
    await generateSummary();
  };

  const handleCopy = () => {
    if (!summary) return;
    const text = [
      `Document Type: ${summary.documentType}`,
      "",
      "Summary:",
      summary.summary,
      "",
      "Key Points:",
      ...summary.keyPoints.map((p) => `- ${p}`),
      ...(summary.parties.length
        ? ["", "Parties:", ...summary.parties.map((p) => `- ${p}`)]
        : []),
      ...(summary.dates.length
        ? ["", "Important Dates:", ...summary.dates.map((d) => `- ${d}`)]
        : []),
      ...(summary.risks.length
        ? ["", "Risks & Concerns:", ...summary.risks.map((r) => `- ${r}`)]
        : []),
      ...(summary.actionItems.length
        ? ["", "Action Items:", ...summary.actionItems.map((a) => `- ${a}`)]
        : []),
    ].join("\n");

    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex justify-end"
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", damping: 28, stiffness: 300 }}
        className="relative w-full max-w-2xl bg-white shadow-2xl overflow-y-auto flex flex-col"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div
                className="p-2.5 rounded-xl"
                style={{ backgroundColor: "rgba(15, 81, 50, 0.08)" }}
              >
                <Sparkles className="h-5 w-5" style={{ color: "var(--color-primary)" }} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-bold text-slate-900">
                    AI Document Analysis
                  </h2>
                  <span className="px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full text-[10px] font-medium uppercase tracking-wider">
                    Powered by AI
                  </span>
                </div>
                {documentTitle && (
                  <p className="text-sm text-slate-500 mt-0.5 flex items-center gap-1.5">
                    <FileText className="h-3.5 w-3.5" />
                    {documentTitle}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 px-6 py-5 space-y-5">
          {/* Loading State */}
          {(loading || generating) && (
            <div className="flex flex-col items-center justify-center py-20 space-y-4">
              <div className="relative">
                <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-emerald-50 to-emerald-100 flex items-center justify-center">
                  <Loader2
                    className="h-8 w-8 animate-spin"
                    style={{ color: "var(--color-primary)" }}
                  />
                </div>
                <div className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full bg-white shadow flex items-center justify-center">
                  <Sparkles className="h-3.5 w-3.5 text-amber-500" />
                </div>
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-slate-700">
                  Analyzing document...
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  Extracting text and generating insights
                </p>
              </div>
              {/* Progress bar animation */}
              <div className="w-48 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <motion.div
                  className="h-full rounded-full"
                  style={{ backgroundColor: "var(--color-primary)" }}
                  initial={{ width: "0%" }}
                  animate={{ width: "85%" }}
                  transition={{ duration: 8, ease: "easeOut" }}
                />
              </div>
            </div>
          )}

          {/* Error State */}
          {!loading && !generating && error && (
            <div className="flex flex-col items-center justify-center py-16 space-y-4">
              <div className="h-14 w-14 rounded-2xl bg-red-50 flex items-center justify-center">
                <AlertTriangle className="h-7 w-7 text-red-500" />
              </div>
              <div className="text-center max-w-sm">
                <p className="text-sm font-semibold text-slate-700">
                  Unable to Analyze
                </p>
                <p className="text-sm text-slate-500 mt-1">{error}</p>
              </div>
              <button
                onClick={handleRegenerate}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl text-white transition-colors"
                style={{ backgroundColor: "var(--color-primary)" }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "var(--color-primary-hover)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "var(--color-primary)";
                }}
              >
                <RefreshCw className="h-4 w-4" />
                Try Again
              </button>
            </div>
          )}

          {/* Summary Content */}
          {!loading && !generating && summary && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="space-y-5"
            >
              {/* Document Type Badge */}
              <div className="flex justify-center">
                <span
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold capitalize ${getTypeColor(summary.documentType)}`}
                >
                  <FileText className="h-4 w-4" />
                  {summary.documentType}
                </span>
              </div>

              {/* Summary */}
              <section className="bg-white border border-slate-200 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <FileText className="h-4 w-4" style={{ color: "var(--color-primary)" }} />
                  Summary
                </h3>
                <div className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">
                  {summary.summary}
                </div>
              </section>

              {/* Key Points */}
              {summary.keyPoints.length > 0 && (
                <section className="bg-white border border-slate-200 rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4" style={{ color: "var(--color-primary)" }} />
                    Key Points
                  </h3>
                  <div className="space-y-2">
                    {summary.keyPoints.map((point, i) => (
                      <div
                        key={i}
                        className="flex items-start gap-3 bg-emerald-50/50 rounded-lg px-3 py-2.5"
                      >
                        <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-slate-700">{point}</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Parties */}
              {summary.parties.length > 0 && (
                <section className="bg-white border border-slate-200 rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <Users className="h-4 w-4" style={{ color: "var(--color-primary)" }} />
                    Parties
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {summary.parties.map((party, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium"
                      >
                        {party}
                      </span>
                    ))}
                  </div>
                </section>
              )}

              {/* Important Dates */}
              {summary.dates.length > 0 && (
                <section className="bg-white border border-slate-200 rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <CalendarDays className="h-4 w-4" style={{ color: "var(--color-primary)" }} />
                    Important Dates
                  </h3>
                  <div className="space-y-2">
                    {summary.dates.map((date, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-3 px-3 py-2.5 bg-slate-50 rounded-lg"
                      >
                        <CalendarDays className="h-4 w-4 text-slate-400 flex-shrink-0" />
                        <span className="text-sm text-slate-700">{date}</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Risks & Concerns */}
              {summary.risks.length > 0 && (
                <section className="bg-white border border-red-100 rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                    Risks & Concerns
                  </h3>
                  <div className="space-y-2">
                    {summary.risks.map((risk, i) => (
                      <div
                        key={i}
                        className="flex items-start gap-3 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2.5"
                      >
                        <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-slate-700">{risk}</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Action Items */}
              {summary.actionItems.length > 0 && (
                <section className="bg-white border border-slate-200 rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <ListChecks className="h-4 w-4" style={{ color: "var(--color-primary)" }} />
                    Action Items
                  </h3>
                  <div className="space-y-2">
                    {summary.actionItems.map((item, i) => (
                      <div
                        key={i}
                        className="flex items-start gap-3 px-3 py-2.5 bg-violet-50/50 rounded-lg"
                      >
                        <div className="h-4 w-4 rounded border-2 border-violet-300 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-slate-700">{item}</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </motion.div>
          )}
        </div>

        {/* Footer */}
        {!loading && !generating && summary && (
          <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-400">
                Generated {formatTimestamp(summary.generatedAt)}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 text-slate-700 text-sm font-medium rounded-xl hover:bg-slate-50 transition-colors"
                >
                  {copied ? (
                    <>
                      <Check className="h-4 w-4 text-emerald-500" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4" />
                      Copy Summary
                    </>
                  )}
                </button>
                <button
                  onClick={handleRegenerate}
                  disabled={generating}
                  className="flex items-center gap-2 px-3 py-2 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-50"
                  style={{ backgroundColor: "var(--color-primary)" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "var(--color-primary-hover)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "var(--color-primary)";
                  }}
                >
                  <RefreshCw className={`h-4 w-4 ${generating ? "animate-spin" : ""}`} />
                  Regenerate
                </button>
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default AiDocumentSummary;
